import {
  Button,
  Badge,
  Card,
  Text,
  tokens,
  Checkbox,
  Popover,
  PopoverTrigger,
  PopoverSurface,
} from '@fluentui/react-components';
import {
  DocumentPdfRegular,
  DocumentCheckmarkRegular,
  LinkRegular,
  MailRegular,
  DeleteRegular,
} from '@fluentui/react-icons';
import { format } from 'date-fns';
import DataTable, { type Column } from '../../components/DataTable';
import { realLinkProps } from '../../components/links/realLinkProps';
import { TableSkeleton } from '../../components/LoadingSkeleton';
import type { DrakeChecklist, DrakeMatchStatus } from '../../types';
import { matchStatusDisplayNames, matchStatusColors } from './types';
import type { ChecklistsTabProps } from './types';

export default function ChecklistsTab({
  checklists,
  checklistsLoading,
  selectedChecklistIds,
  setSelectedChecklistIds,
  bulkDeleteConfirmOpen,
  setBulkDeleteConfirmOpen,
  bulkDeleteMutation,
  setSelectedChecklist,
  setDetailDialogOpen,
  setMatchDialogOpen,
  navigate,
}: ChecklistsTabProps) {
  const columns: Column<DrakeChecklist>[] = [
    {
      key: 'select',
      header: '',
      width: '40px',
      render: (row) => (
        <Checkbox
          checked={selectedChecklistIds.has(row.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={(_, data) => {
            const newSet = new Set(selectedChecklistIds);
            if (data.checked) {
              newSet.add(row.id);
            } else {
              newSet.delete(row.id);
            }
            setSelectedChecklistIds(newSet);
          }}
        />
      ),
    },
    {
      key: 'client',
      header: 'Client Name',
      width: '200px',
      render: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Text weight="semibold">{row.extractedClientInfo?.name || 'Unknown'}</Text>
          {row.extractedClientInfo?.spouseName && (
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              & {row.extractedClientInfo.spouseName}
            </Text>
          )}
        </div>
      ),
    },
    {
      key: 'matchStatus',
      header: 'Status',
      width: '140px',
      render: (row) => (
        <Badge
          appearance="filled"
          style={{ backgroundColor: matchStatusColors[row.matchStatus as DrakeMatchStatus] }}
        >
          {matchStatusDisplayNames[row.matchStatus as DrakeMatchStatus]}
        </Badge>
      ),
    },
    {
      key: 'items',
      header: 'Checklist Items',
      width: '120px',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <DocumentCheckmarkRegular />
          <Text>{row.checklistItems?.length || 0} items</Text>
        </div>
      ),
    },
    {
      key: 'confidence',
      header: 'Confidence',
      width: '100px',
      render: (row) => (
        <Text>{Math.round((row.extractionMetadata?.confidence || 0) * 100)}%</Text>
      ),
    },
    {
      key: 'extractedAt',
      header: 'Extracted',
      width: '120px',
      render: (row) => (
        <Text>{row.extractionMetadata?.extractedAt
          ? format(new Date(row.extractionMetadata.extractedAt), 'MMM d, yyyy')
          : '-'}</Text>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '140px',
      render: (row) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            appearance="subtle"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedChecklist(row);
              setDetailDialogOpen(true);
            }}
          >
            View
          </Button>
          {row.matchStatus !== 'matched' && (
            <Button
              size="small"
              appearance="primary"
              icon={<LinkRegular />}
              onClick={() => {
                setSelectedChecklist(row);
                setMatchDialogOpen(true);
              }}
            >
              Match
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {checklistsLoading ? (
        <div style={{ padding: '48px' }}>
          <TableSkeleton />
        </div>
      ) : checklists && checklists.length > 0 ? (
        <>
          {/* Bulk Action Bar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 16px',
            marginBottom: '8px',
            backgroundColor: selectedChecklistIds.size > 0 ? tokens.colorBrandBackground2 : tokens.colorNeutralBackground3,
            borderRadius: '4px',
          }}>
            <Checkbox
              checked={checklists.length > 0 && selectedChecklistIds.size === checklists.length}
              onChange={(_, data) => {
                if (data.checked) {
                  setSelectedChecklistIds(new Set(checklists.map(c => c.id)));
                } else {
                  setSelectedChecklistIds(new Set());
                }
              }}
              label={selectedChecklistIds.size > 0 ? `${selectedChecklistIds.size} selected` : 'Select all'}
            />
            {selectedChecklistIds.size > 0 && (() => {
              const matchedChecklists = checklists.filter(c => selectedChecklistIds.has(c.id) && c.matchStatus === 'matched');
              const matchedClientIds = matchedChecklists.filter(c => c.clientId).map(c => c.clientId);
              const outreachHref = matchedClientIds.length > 0 ? `/outreach?clientIds=${matchedClientIds.join(',')}` : undefined;
              return (
              <>
                {outreachHref ? (
                  <Button as="a" appearance="primary" icon={<MailRegular />} {...realLinkProps(outreachHref, { navigate })}>
                    Send Outreach ({matchedChecklists.length})
                  </Button>
                ) : (
                  <Button
                    appearance="primary"
                    icon={<MailRegular />}
                    onClick={() => alert('No matched clients selected. Only matched checklists can receive outreach.')}
                  >
                    Send Outreach ({matchedChecklists.length})
                  </Button>
                )}
                <Popover open={bulkDeleteConfirmOpen} onOpenChange={(_, data) => setBulkDeleteConfirmOpen(data.open)}>
                  <PopoverTrigger disableButtonEnhancement>
                    <Button
                      appearance="subtle"
                      icon={<DeleteRegular style={{ color: tokens.colorPaletteRedForeground1 }} />}
                    >
                      Delete
                    </Button>
                  </PopoverTrigger>
                  <PopoverSurface style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <Text weight="semibold">Delete {selectedChecklistIds.size} checklist(s)?</Text>
                      <Text size={200}>This action cannot be undone.</Text>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <Button appearance="subtle" onClick={() => setBulkDeleteConfirmOpen(false)}>
                          Cancel
                        </Button>
                        <Button
                          appearance="primary"
                          style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
                          disabled={bulkDeleteMutation.isPending}
                          onClick={() => {
                            bulkDeleteMutation.mutate(Array.from(selectedChecklistIds));
                          }}
                        >
                          {bulkDeleteMutation.isPending ? 'Deleting...' : 'Delete'}
                        </Button>
                      </div>
                    </div>
                  </PopoverSurface>
                </Popover>
                <Button
                  appearance="subtle"
                  onClick={() => setSelectedChecklistIds(new Set())}
                >
                  Clear Selection
                </Button>
              </>
              );
            })()}
          </div>
          <DataTable
            columns={columns}
            data={checklists}
            rowKey="id"
            onRowClick={(row) => {
              setSelectedChecklist(row);
              setDetailDialogOpen(true);
            }}
            paginated
          />
        </>
      ) : (
        <Card style={{ padding: '48px', textAlign: 'center' }}>
          <DocumentPdfRegular style={{ fontSize: '48px', color: tokens.colorNeutralForeground3 }} />
          <Text as="p" style={{ marginTop: '16px' }}>No checklists extracted yet</Text>
          <Text as="p" style={{ color: tokens.colorNeutralForeground3 }}>
            Upload Drake Proforma PDFs to get started
          </Text>
        </Card>
      )}
    </div>
  );
}
